import React from "react"
import cx from "classnames"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderActionsProps = {
  children?: React.ReactNode
  className?: string
}

export type PageSecondaryHeaderActionsSlotProps = {
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderSecondaryActionsStyles = [
  "col-start-3 flex items-center justify-end gap-2"
]

// Use `contents` to allow children to participate in the parent flex layout.
// Identical to passing actions directly into `PageSecondaryHeader.Actions`.

// Show compact actions until `@4xl/page-header`
const pageSecondaryHeaderActionsCompactStyles =
  "contents @4xl/page-header:hidden"
// Show full actions above `@4xl/page-header`
const pageSecondaryHeaderActionsFullStyles = "hidden @4xl/page-header:contents"

// =============================================================================
// Slots
// =============================================================================

/**
 * Compact actions shown until `@4xl/page-header`.
 */
function PageSecondaryHeaderActionsCompact({
  children,
  className
}: PageSecondaryHeaderActionsSlotProps) {
  return (
    <div
      className={cx(pageSecondaryHeaderActionsCompactStyles, className)}
      data-slot="secondary-actions-compact"
    >
      {children}
    </div>
  )
}

PageSecondaryHeaderActionsCompact.displayName =
  "PageSecondaryHeader.Actions.Compact"

/**
 * Full actions shown above `@4xl/page-header`.
 */
function PageSecondaryHeaderActionsFull({
  children,
  className
}: PageSecondaryHeaderActionsSlotProps) {
  return (
    <div
      className={cx(pageSecondaryHeaderActionsFullStyles, className)}
      data-slot="secondary-actions-full"
    >
      {children}
    </div>
  )
}

PageSecondaryHeaderActionsFull.displayName = "PageSecondaryHeader.Actions.Full"

// =============================================================================
// Component
// =============================================================================

interface PageSecondaryHeaderActionsComponent {
  (props: PageSecondaryHeaderActionsProps): React.JSX.Element
  /** Actions shown until `@4xl/page-header`. */
  Compact: typeof PageSecondaryHeaderActionsCompact
  /** Actions shown above `@4xl/page-header`. */
  Full: typeof PageSecondaryHeaderActionsFull
}

/**
 * Secondary page header actions.
 *
 * Compose `PageSecondaryHeader.Actions.Compact` (shown until `@4xl/page-header`) and
 * `PageSecondaryHeader.Actions.Full` (shown above `@4xl/page-header`) to render different actions
 * to prevent unwanted content wrapping.
 *
 * @example
 * <PageSecondaryHeader.Actions>
 *   <PageSecondaryHeader.Actions.Compact>
 *     <OldIconButton is="secondary" icon="archive" label="Archive" onClick={onArchive} />
 *     <OldIconButton is="primary" icon="plus" label="New snippet" onClick={onCreate} />
 *   </PageSecondaryHeader.Actions.Compact>
 *   <PageSecondaryHeader.Actions.Full>
 *     <OldButton is="secondary" icon="archive" onClick={onArchive}>Archive</OldButton>
 *     <OldButton is="primary" icon="plus" onClick={onCreate}>New snippet</OldButton>
 *   </PageSecondaryHeader.Actions.Full>
 * </PageSecondaryHeader.Actions>
 */
function PageSecondaryHeaderActionsRoot({
  children,
  className
}: PageSecondaryHeaderActionsProps) {
  return (
    <div
      className={cx(pageSecondaryHeaderSecondaryActionsStyles, className)}
      data-slot="secondary-actions"
    >
      {children}
    </div>
  )
}

export const PageSecondaryHeaderActions: PageSecondaryHeaderActionsComponent =
  Object.assign(PageSecondaryHeaderActionsRoot, {
    Compact: PageSecondaryHeaderActionsCompact,
    Full: PageSecondaryHeaderActionsFull
  } as const)
